import type {RecordPlugin} from '@rrweb/types';
import * as rrweb from 'rrweb';
import type {ConsoleLogPayload} from './actionSummaryTypes';
import {accumulateConsole} from './sessionSummaryAccumulator';
import {redactString, redactConsoleArg, isSensitiveKey} from './consoleRedactor';

const LEVEL_MAP = {log: 'L', info: 'I', warn: 'W', error: 'E'} as const;
const LEVELS = Object.keys(LEVEL_MAP) as (keyof typeof LEVEL_MAP)[];
const MAX_ARG_LENGTH = 200;
const MAX_OBJECT_LENGTH = 500;

const REDACTED = '[REDACTED]';

function serializeArg(arg: unknown): string {
	if (arg === null) return 'null';
	if (arg === undefined) return 'undefined';

	switch (typeof arg) {
		case 'string': {
			const redacted = redactString(arg);
			return redacted.length > MAX_ARG_LENGTH ? redacted.slice(0, MAX_ARG_LENGTH) + '...' : redacted;
		}
		case 'number':
			return redactString(String(arg));
		case 'boolean':
			return String(arg);
		case 'function':
			return '[Function]';
		case 'symbol':
			return redactString(arg.toString());
		case 'object': {
			if (arg instanceof Error) {
				// Error messages can include redact-worthy substrings
				// (e.g. "401 from https://...?token=eyJ...").
				return redactString(`${arg.name}: ${arg.message}`);
			}
			if (arg instanceof Element || arg instanceof Node) {
				return '[Element]';
			}
			try {
				// Pre-redact the structure so JSON.stringify only ever sees
				// already-cleaned values. The replacer below handles the
				// non-JSON-able edge cases (functions, symbols, DOM nodes).
				const cleaned = redactConsoleArg(arg);
				// The final text is redacted again, before it is cut (OWA-877):
				// numbers nested in the value are not strings, so the walk
				// above leaves them as they are.
				const json = redactString(JSON.stringify(cleaned, (key, value) => {
					if (typeof value === 'function') return '[Function]';
					if (typeof value === 'symbol') return value.toString();
					if (value instanceof Element || value instanceof Node) return '[Element]';
					// Defensive second pass — if a sensitive key slipped past
					// (unlikely after redactConsoleArg), redact at stringify time.
					if (typeof key === 'string' && isSensitiveKey(key) && value !== REDACTED) {
						return REDACTED;
					}
					return value;
				}, 0));
				if (json && json.length > MAX_OBJECT_LENGTH) {
					return json.slice(0, MAX_OBJECT_LENGTH) + '...';
				}
				return json || redactString(String(arg));
			} catch {
				// Fallback to a redacted toString
				return redactString(String(arg));
			}
		}
		default:
			return redactString(String(arg));
	}
}

export function createConsoleRecordPlugin(): RecordPlugin {
	return {
		name: 'console-capture',
		observer(cb, win) {
			const originals: Partial<Record<string, (...args: any[]) => void>> = {};
			const con = win.console;
			let emitting = false;

			for (const level of LEVELS) {
				originals[level] = con[level];
				con[level] = (...args: any[]) => {
					// Guard against re-entrancy (serializeArg could trigger console calls)
					if (!emitting) {
						emitting = true;
						try {
							const payload: ConsoleLogPayload = {
								l: LEVEL_MAP[level],
								a: args.map(serializeArg),
							};
							rrweb.addCustomEvent('c', payload);
							accumulateConsole(LEVEL_MAP[level]);
						} catch {
							// addCustomEvent throws if called before recording is active;
							// silently drop these early console events
						} finally {
							emitting = false;
						}
					}
					originals[level]!.apply(con, args);
				};
			}

			return () => {
				for (const level of LEVELS) {
					if (originals[level]) {
						con[level] = originals[level] as any;
					}
				}
			};
		},
		options: {},
	};
}
